import React, { useState, useEffect, useRef } from 'react';
import {
  Card,
  CardContent,
  Box,
  Typography,
  TextField,
  Chip,
  IconButton,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Snackbar,
  Alert,
  Tooltip
} from '@mui/material';
import {
  CheckCircle,
  RadioButtonUnchecked,
  Save as SaveIcon,
  Close as CloseIcon
} from '@mui/icons-material';

interface EditableFeatureCardProps {
  feature: {
    _id?: string;
    name: string;
    title?: string;
    description: string;
    userStory?: string;
    userStoryDetails?: {
      role?: string;
      need?: string;
      benefit?: string;
    };
    status: 'planned' | 'in-progress' | 'completed' | 'deferred';
    priority: 'critical' | 'high' | 'medium' | 'low';
  };
  index: number;
  onSave: (index: number, updatedFeature: any) => Promise<void>;
  isEditing: boolean;
  onEditStart: (index: number) => void;
  onEditCancel: () => void;
  getPriorityColor: (priority: string) => string;
}

const EditableFeatureCard: React.FC<EditableFeatureCardProps> = ({
  feature,
  index,
  onSave,
  isEditing,
  onEditStart,
  onEditCancel,
  getPriorityColor
}) => {
  const [editedFeature, setEditedFeature] = useState(feature);
  const [saving, setSaving] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const cardRef = useRef<HTMLDivElement>(null);
  const nameInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setEditedFeature(feature);
  }, [feature]);

  useEffect(() => {
    if (isEditing && nameInputRef.current) {
      nameInputRef.current.focus();
    }
  }, [isEditing]);

  // Click outside to close
  useEffect(() => {
    if (!isEditing || saving) return; // Don't handle clicks while saving

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      
      // Check if click is inside the card
      if (cardRef.current && !cardRef.current.contains(target)) {
        // Check if click is on a MUI dropdown/menu (they render outside the card)
        const isClickOnDropdown = (target as Element).closest('.MuiPopover-root, .MuiMenu-root, .MuiModal-root');
        
        if (isClickOnDropdown) {
          // Don't close if clicking on dropdown
          return;
        }
        
        // Check if there are unsaved changes
        const hasChanges = JSON.stringify(editedFeature) !== JSON.stringify(feature);
        
        if (hasChanges) {
          // Optionally, you could show a confirmation dialog here
          // For now, we'll just cancel (revert changes)
          handleCancel();
        } else {
          onEditCancel();
        }
      }
    };

    // Add event listener
    document.addEventListener('mousedown', handleClickOutside);

    // Cleanup
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isEditing, editedFeature, feature, saving]);

  const handleCardClick = () => {
    if (!isEditing) {
      onEditStart(index);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      console.log('EditableFeatureCard: Saving feature with status:', editedFeature.status);
      console.log('EditableFeatureCard: Full feature data:', editedFeature);
      
      await onSave(index, editedFeature);
      
      console.log('EditableFeatureCard: Save completed successfully');
      setShowSuccess(true);
      
      // Wait a bit before closing to ensure save is complete
      await new Promise(resolve => setTimeout(resolve, 300));
      onEditCancel();
    } catch (error: any) {
      console.error('EditableFeatureCard: Error saving feature:', error);
      setErrorMessage(error.response?.data?.message || error.message || 'Failed to save feature');
      setShowError(true);
      setSaving(false); // Re-enable form on error
    }
  };

  const handleCancel = () => {
    setEditedFeature(feature);
    onEditCancel();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      handleCancel();
    } else if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
      handleSave();
    }
  };

  if (isEditing) {
    return (
      <Card
        ref={cardRef}
        sx={{
          height: '100%',
          borderLeft: `4px solid ${getPriorityColor(editedFeature.priority)}`,
          position: 'relative',
          boxShadow: 4
        }}
      >
        <CardContent>
          {/* Action Buttons */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mb: 2 }}>
            <Tooltip title={saving ? "Saving..." : "Save changes (Ctrl+Enter)"}>
              <IconButton
                color="primary"
                onClick={handleSave}
                disabled={saving}
                sx={{
                  bgcolor: saving ? 'grey.400' : 'success.main',
                  color: 'white',
                  '&:hover': { bgcolor: saving ? 'grey.400' : 'success.dark' }
                }}
              >
                <SaveIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Cancel (Esc)">
              <IconButton
                color="error"
                onClick={handleCancel}
                disabled={saving}
              >
                <CloseIcon />
              </IconButton>
            </Tooltip>
          </Box>
          
          {/* Saving Indicator */}
          {saving && (
            <Typography variant="caption" color="primary" sx={{ display: 'block', textAlign: 'center', mb: 2 }}>
              Saving changes...
            </Typography>
          )}
          {/* Feature Name */}
          <TextField
            inputRef={nameInputRef}
            fullWidth
            label="Feature Name"
            value={editedFeature.name}
            onChange={(e) => setEditedFeature({ ...editedFeature, name: e.target.value })}
            onKeyDown={handleKeyDown}
            disabled={saving}
            sx={{ mb: 2 }}
            required
          />

          {/* Feature Description */}
          <TextField
            fullWidth
            label="Description"
            value={editedFeature.description}
            onChange={(e) => setEditedFeature({ ...editedFeature, description: e.target.value })}
            onKeyDown={handleKeyDown}
            disabled={saving}
            multiline
            rows={3}
            sx={{ mb: 2 }}
            required
          />

          {/* User Story Section */}
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold', color: 'primary.main' }}>
            User Story
          </Typography>

          {/* Role */}
          <TextField
            fullWidth
            label="As a (Role)"
            value={editedFeature.userStoryDetails?.role || ''}
            onChange={(e) => {
              const newRole = e.target.value;
              const newUserStoryDetails = {
                ...editedFeature.userStoryDetails,
                role: newRole,
                need: editedFeature.userStoryDetails?.need || '',
                benefit: editedFeature.userStoryDetails?.benefit || ''
              };
              const newUserStory = `As a ${newRole}, I need ${newUserStoryDetails.need}, so that ${newUserStoryDetails.benefit}.`;
              setEditedFeature({
                ...editedFeature,
                userStoryDetails: newUserStoryDetails,
                userStory: newUserStory
              });
            }}
            onKeyDown={handleKeyDown}
            disabled={saving}
            sx={{ mb: 2 }}
            placeholder="ODOS-IV Integration Engineer"
          />

          {/* Need */}
          <TextField
            fullWidth
            label="I need (Action/Need)"
            value={editedFeature.userStoryDetails?.need || ''}
            onChange={(e) => {
              const newNeed = e.target.value;
              const newUserStoryDetails = {
                ...editedFeature.userStoryDetails,
                role: editedFeature.userStoryDetails?.role || '',
                need: newNeed,
                benefit: editedFeature.userStoryDetails?.benefit || ''
              };
              const newUserStory = `As a ${newUserStoryDetails.role}, I need ${newNeed}, so that ${newUserStoryDetails.benefit}.`;
              setEditedFeature({
                ...editedFeature,
                userStoryDetails: newUserStoryDetails,
                userStory: newUserStory
              });
            }}
            onKeyDown={handleKeyDown}
            disabled={saving}
            multiline
            rows={2}
            sx={{ mb: 2 }}
            placeholder="the thought leadership on integration of distinct systems"
          />

          {/* Benefit */}
          <TextField
            fullWidth
            label="So that (Benefit/Value)"
            value={editedFeature.userStoryDetails?.benefit || ''}
            onChange={(e) => {
              const newBenefit = e.target.value;
              const newUserStoryDetails = {
                ...editedFeature.userStoryDetails,
                role: editedFeature.userStoryDetails?.role || '',
                need: editedFeature.userStoryDetails?.need || '',
                benefit: newBenefit
              };
              const newUserStory = `As a ${newUserStoryDetails.role}, I need ${newUserStoryDetails.need}, so that ${newBenefit}.`;
              setEditedFeature({
                ...editedFeature,
                userStoryDetails: newUserStoryDetails,
                userStory: newUserStory
              });
            }}
            onKeyDown={handleKeyDown}
            disabled={saving}
            multiline
            rows={2}
            sx={{ mb: 2 }}
            placeholder="we can seamlessly integrate systems and improve interoperability"
          />

          {/* Status and Priority */}
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={editedFeature.status}
                onChange={(e) => setEditedFeature({ ...editedFeature, status: e.target.value as any })}
                label="Status"
                disabled={saving}
              >
                <MenuItem value="planned">Planned</MenuItem>
                <MenuItem value="in-progress">In Progress</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="deferred">Deferred</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth size="small">
              <InputLabel>Priority</InputLabel>
              <Select
                value={editedFeature.priority}
                onChange={(e) => setEditedFeature({ ...editedFeature, priority: e.target.value as any })}
                label="Priority"
                disabled={saving}
              >
                <MenuItem value="critical">Critical</MenuItem>
                <MenuItem value="high">High</MenuItem>
                <MenuItem value="medium">Medium</MenuItem>
                <MenuItem value="low">Low</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {/* Help Text */}
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            Press <strong>Ctrl+Enter</strong> (or <strong>Cmd+Enter</strong>) to save, <strong>Esc</strong> to cancel, or click outside to close
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card
      ref={cardRef}
      onClick={handleCardClick}
      sx={{
        height: '100%',
        borderLeft: `4px solid ${getPriorityColor(feature.priority)}`,
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        '&:hover': {
          boxShadow: 4,
          transform: 'translateY(-2px)',
          bgcolor: 'rgba(0, 51, 102, 0.02)'
        },
        '&:focus': {
          outline: '2px solid #003366',
          outlineOffset: '2px'
        }
      }}
      tabIndex={0}
      role="button"
      aria-label={`Edit ${feature.name}`}
      onKeyPress={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleCardClick();
        }
      }}
    >
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Typography variant="h6" component="h3">
            {feature.title || feature.name}
          </Typography>
          {feature.status === 'completed' ? (
            <CheckCircle color="success" />
          ) : (
            <RadioButtonUnchecked color="action" />
          )}
        </Box>
        
        {/* User Story */}
        {feature.userStory && (
          <Box sx={{ mb: 2, p: 2, bgcolor: 'rgba(0, 51, 102, 0.05)', borderRadius: 1, borderLeft: '3px solid #003366' }}>
            <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'primary.main' }}>
              <strong>As a</strong> {feature.userStoryDetails?.role || '...'}, <strong>I need</strong> {feature.userStoryDetails?.need || '...'}, <strong>so that</strong> {feature.userStoryDetails?.benefit || '...'}.
            </Typography>
          </Box>
        )}
        
        <Typography variant="body2" color="text.secondary" paragraph>
          {feature.description}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Chip label={feature.status} size="small" />
          <Chip
            label={feature.priority}
            size="small"
            sx={{ bgcolor: getPriorityColor(feature.priority), color: 'white' }}
          />
        </Box>
        
        {/* Click to edit hint */}
        <Typography 
          variant="caption" 
          color="text.secondary" 
          sx={{ 
            display: 'block', 
            mt: 2, 
            fontStyle: 'italic',
            opacity: 0.7
          }}
        >
          Click anywhere to edit
        </Typography>
      </CardContent>

      {/* Success Notification */}
      <Snackbar
        open={showSuccess}
        autoHideDuration={2000}
        onClose={() => setShowSuccess(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity="success" onClose={() => setShowSuccess(false)}>
          Feature saved successfully!
        </Alert>
      </Snackbar>

      {/* Error Notification */}
      <Snackbar
        open={showError}
        autoHideDuration={4000}
        onClose={() => setShowError(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity="error" onClose={() => setShowError(false)}>
          {errorMessage}
        </Alert>
      </Snackbar>
    </Card>
  );
};

export default EditableFeatureCard;
